/**
 *  @class
 *  @function FreeShippingProgressBar
 */

if (!customElements.get('free-shipping-progress-bar')) {
    let currentPercent = 0; // Store the current percentage globally

    class FreeShippingProgressBar extends HTMLElement {
      constructor() {
        super();
      }

      async connectedCallback() {
        await this.fetchCartData();
        this.updateStatus();
      }

      async fetchCartData() {
        const data = await window.CartState.get();
        this.discount_codes = data.discount_codes || []
        return data;
      }

      updateStatus() {
        const remainingInfo = this.querySelector('.free-shipping__info-remaining');
        const eligibilityInfo = this.querySelector('.free-shipping__info-eligibility');
        const giftNode = this.querySelector('.free-shipping__node--gift');
        const shippingNode = this.querySelector('.free-shipping__node--shipping');
        const giftCaption = this.querySelector('.free-shipping__caption--gift');
        const shippingCaption = this.querySelector('.free-shipping__caption--shipping');

        const cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
        const rate = Shopify.currency.rate || 1;
        const shippingThreshold = Math.round(parseInt(this.dataset.shippingAmount, 10) * rate);
        const giftEnabled = this.dataset.giftEnabled === 'true';
        const giftThreshold = giftEnabled ? Math.round(parseInt(this.dataset.giftAmount, 10) * rate) : 0;

        const discount_codes = JSON.stringify(this.discount_codes);
        const hasDeliveryCoupon = discount_codes?.includes('FRETE');
        const isShippingEligible = cartTotal >= shippingThreshold || hasDeliveryCoupon;
        const isGiftEligible = giftEnabled && cartTotal >= giftThreshold;

        const shippingPosition = parseFloat(this.dataset.shippingPosition) || 100;
        const giftPosition = parseFloat(this.dataset.giftPosition) || 100;

        const currencyFormat = window.theme.settings.money_with_currency_format || "${{amount}}";

        // Threshold captions are set here (rather than rendered server-side) so they
        // reflect the shopper's selected presentment currency, same as the thresholds
        // themselves (which are already converted via Shopify.currency.rate above).
        const shippingCaptionAmount = this.querySelector('.free-shipping__caption--shipping .free-shipping__caption-amount');
        if (shippingCaptionAmount) shippingCaptionAmount.textContent = formatMoney(shippingThreshold, currencyFormat);
        const giftCaptionAmount = this.querySelector('.free-shipping__caption--gift .free-shipping__caption-amount');
        if (giftCaptionAmount) giftCaptionAmount.textContent = formatMoney(giftThreshold, currencyFormat);

        if (remainingInfo) {
          if (!isShippingEligible) {
            remainingInfo.innerHTML = this.formatMessage(this.dataset.shippingMessage, shippingThreshold - cartTotal, currencyFormat);
          } else if (giftEnabled && !isGiftEligible) {
            remainingInfo.innerHTML = this.formatMessage(this.dataset.giftMessage, giftThreshold - cartTotal, currencyFormat);
          }
        }

        const isFullyEligible = isShippingEligible && (!giftEnabled || isGiftEligible);
        if (eligibilityInfo) {
          eligibilityInfo.textContent = isGiftEligible ? this.dataset.giftEligibleText : this.dataset.shippingEligibleText;
        }
        if (remainingInfo) remainingInfo.style.display = isFullyEligible ? 'none' : 'block';
        if (eligibilityInfo) eligibilityInfo.style.display = isFullyEligible ? 'block' : 'none';

        if (shippingNode) shippingNode.classList.toggle('is-reached', isShippingEligible);
        if (giftNode) giftNode.classList.toggle('is-reached', isGiftEligible);
        if (shippingCaption) shippingCaption.classList.toggle('is-reached', isShippingEligible);
        if (giftCaption) giftCaption.classList.toggle('is-reached', isGiftEligible);

        this.celebrateIfNewlyReached('fsCelebrated-shipping', isShippingEligible, shippingNode);
        this.celebrateIfNewlyReached('fsCelebrated-gift', isGiftEligible, giftNode);

        this.animateProgressBar(this.getFillPercent({
          cartTotal,
          shippingThreshold,
          giftThreshold,
          giftEnabled,
          shippingPosition,
          giftPosition,
          isShippingEligible,
        }));
      }

      // Fill maps to the fixed node positions in two segments, so the bar edge
      // always lines up with the node it is currently progressing towards.
      // Once the last mark is reached, the bar completes to 100%. A coupon that
      // grants free shipping below the real threshold (isShippingEligible) still
      // starts the fill from the shipping node, matching its "reached" state.
      getFillPercent({ cartTotal, shippingThreshold, giftThreshold, giftEnabled, shippingPosition, giftPosition, isShippingEligible }) {
        const effectiveTotal = isShippingEligible ? Math.max(cartTotal, shippingThreshold) : cartTotal;
        if (!giftEnabled || shippingThreshold <= 0) {
          return Math.min(effectiveTotal / Math.max(shippingThreshold, 1), 1) * 100;
        }
        if (effectiveTotal >= giftThreshold) {
          return 100;
        }
        if (effectiveTotal <= shippingThreshold) {
          return Math.min(effectiveTotal / shippingThreshold, 1) * shippingPosition;
        }
        const segmentRange = Math.max(giftThreshold - shippingThreshold, 1);
        const segmentProgress = Math.min((effectiveTotal - shippingThreshold) / segmentRange, 1);
        return shippingPosition + segmentProgress * (giftPosition - shippingPosition);
      }

      // Plays the pop animation only on the transition into "reached" (tracked in
      // sessionStorage, since the element is recreated on every cart re-render),
      // so it doesn't replay on every quantity change while already past the mark.
      celebrateIfNewlyReached(storageKey, isReached, node) {
        if (!node) return;
        if (!isReached) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        if (sessionStorage.getItem(storageKey) === 'true') return;
        sessionStorage.setItem(storageKey, 'true');
        node.classList.remove('just-reached');
        void node.offsetWidth;
        node.classList.add('just-reached');
        node.addEventListener('animationend', () => node.classList.remove('just-reached'), { once: true });
      }

      formatMessage(template, remainingAmount, currencyFormat) {
        const amount = formatMoney(Math.max(remainingAmount, 0), currencyFormat);
        return template.replace('{{ amount }}', `<strong>${amount}</strong>`);
      }

      animateProgressBar(targetPercent) {
        const startPercent = currentPercent;
        const duration = 200;
        const startTime = performance.now();

        const ease = (t) => t * (2 - t);

        const animate = (time) => {
          const elapsed = time - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = ease(progress);

          currentPercent = startPercent + (targetPercent - startPercent) * easedProgress;
          this.style.setProperty('--percent', `${currentPercent}%`);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            currentPercent = targetPercent;
            this.style.setProperty('--percent', `${currentPercent}%`);
          }
        };

        requestAnimationFrame(animate);
      }
    }

    customElements.define('free-shipping-progress-bar', FreeShippingProgressBar);
}
